// ============================================================
// Platform Control · Storage — bucket readiness, file flows,
// and storage health. Shows known required buckets and navigates
// to the Files surface for real verification.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ViewShell, { Card, Row, Section } from './_ViewShell'
import { SHELL_TOKENS, text } from '../../../shell/styles'
import { api, isAuthError } from '../../../api/client'

interface BucketCheck {
  name: string
  purpose: string
  visibility: string
  exists: boolean
  error: string | null
}

export default function StorageView() {
  const navigate = useNavigate()
  const [buckets, setBuckets] = useState<BucketCheck[]>([])
  const [loading, setLoading] = useState(false)
  const [checked, setChecked] = useState(false)

  const checkBuckets = useCallback(async () => {
    setLoading(true)
    try {
      const result = await api.admin.storageStatus()
      setBuckets(result.storage.buckets)
      setChecked(true)
    } catch (err) {
      if (!isAuthError(err)) console.warn('[StorageView] status check failed:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void checkBuckets() }, [checkBuckets])

  const allPresent = checked && buckets.length > 0 && buckets.every((b) => b.exists)
  const missingCount = buckets.filter((b) => !b.exists).length

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · STORAGE"
      title="Storage"
      description="Bucket readiness, file flows, and storage health. Object reads go through signed URLs scoped per session."
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={checkBuckets}
            disabled={loading}
            style={{
              padding: '10px 16px', borderRadius: 10,
              border: `1px solid ${SHELL_TOKENS.border}`,
              background: 'transparent', color: SHELL_TOKENS.ink,
              fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', fontFamily: 'var(--font-mono)',
              cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Checking...' : 'Verify Buckets'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/square/command-center/workspaces/vault')}
            style={{
              padding: '10px 16px', borderRadius: 10,
              border: `1px solid ${SHELL_TOKENS.border}`,
              background: 'transparent', color: SHELL_TOKENS.ink,
              fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', fontFamily: 'var(--font-mono)',
              cursor: 'pointer',
            }}
          >
            Open Files
          </button>
        </div>
      }
    >
      {checked && (
        <Section label="BUCKET STATUS">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 14 }}>
            <StatTile label="Required" value={String(buckets.length)} />
            <StatTile label="Present" value={String(buckets.filter((b) => b.exists).length)} tone={allPresent ? SHELL_TOKENS.green : SHELL_TOKENS.amber} />
            <StatTile label="Missing" value={String(missingCount)} tone={missingCount > 0 ? SHELL_TOKENS.red : SHELL_TOKENS.green} />
          </div>
        </Section>
      )}

      <Section label="REQUIRED BUCKETS">
        <Card>
          {(checked ? buckets : [
            { name: 'deliverables', purpose: 'Workspace exports and deliverables', visibility: 'private', exists: false, error: null },
            { name: 'evidence', purpose: 'OLIVIUM sealed evidence and audit artifacts', visibility: 'private', exists: false, error: null },
            { name: 'assets', purpose: 'Site builder assets and media', visibility: 'public', exists: false, error: null },
          ]).map((b) => (
            <Row
              key={b.name}
              label={b.name}
              value={
                checked
                  ? <span style={{ color: b.exists ? SHELL_TOKENS.green : SHELL_TOKENS.red, fontWeight: 700 }}>
                      {b.exists ? 'EXISTS' : 'MISSING'}
                    </span>
                  : b.visibility
              }
              hint={b.purpose}
            />
          ))}
        </Card>
      </Section>

      <Section label="SETUP">
        <Card>
          <Row label="Create buckets" value="node scripts/setup-storage.mjs" hint="one-time, idempotent" />
          <Row label="Provider" value="Supabase Storage" hint="S3-compatible API" />
          <Row label="Access model" value="signed URLs" hint="scoped per session, time-limited" />
        </Card>
      </Section>
    </ViewShell>
  )
}

function StatTile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 10,
      background: SHELL_TOKENS.surface, border: `1px solid ${SHELL_TOKENS.border}`,
    }}>
      <div style={{ ...text.kicker, fontSize: 8, letterSpacing: '0.18em', color: SHELL_TOKENS.inkFaint, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: tone || SHELL_TOKENS.ink, fontFamily: 'var(--font-mono)' }}>
        {value}
      </div>
    </div>
  )
}
